import { SortOrder } from 'src/services/common.types';
import { PageOptions, SortConfig } from 'src/services/api.types';

/**
 * Shared search behaviour for the "pick one record" Autocompletes (JT1-1586): add a candidate to a
 * job, link a contact or company, add to a list.
 *
 * These pickers used to send `search_query`, the full list search that also scans every note and
 * transcript. On a large org that ran into the 20s statement ceiling (12 of 15 Add Candidate searches
 * in prod, 7-13 Sep 2026) and returned the 5 newest records mentioning the word anywhere. They now
 * send the lean lists' `column_filters` name/email search instead: direct-column LIKEs, no subqueries,
 * already carrying hundreds of list-grid searches a day at p90 < 2s. The server tokenizes the value,
 * so "smith john" matches John Smith.
 */

export const PICKER_PAGE: PageOptions = { page: 1, per_page: 20 };

type PickerTranslate = (key: string, options?: any) => any;

/**
 * Column filters for a picker search. Blank sends nothing, so the list falls back to newest first.
 * Text containing `@` searches the email column when the entity has one (candidates, contacts).
 */
export function buildPickerColumnFilters(
  query: string | undefined,
  { email = false }: { email?: boolean } = {}
): Record<string, string> | undefined {
  const value = query?.trim();
  if (!value) return undefined;

  return email && value.includes('@') ? { email: value } : { name: value };
}

/** Name order once something is typed; the list's default (newest first) otherwise. */
export function pickerSortConfig(query: string | undefined): SortConfig | undefined {
  return query?.trim() ? { sort_by: 'name', sort_order: SortOrder.ASC } : undefined;
}

/** "Showing 20 of N — type more to narrow" when the server matched more than it returned. */
export function pickerHelperText(
  t: PickerTranslate,
  data: { results: unknown[]; count: number } | undefined
): string | undefined {
  if (!data || data.count <= data.results.length) return undefined;

  return String(t('common.picker.showing_of', { shown: data.results.length, total: data.count }));
}

/** A failed search must not read as "No options" (JT1-1586). */
export function pickerNoOptionsText(t: PickerTranslate, isError: boolean): string {
  return String(t(isError ? 'common.picker.search_error' : 'common.picker.no_results'));
}
